import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ShowDialogsComponent } from 'src/app/components/show-dialogs/show-dialogs.component';
import { SettingsService } from 'src/app/services/settings.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { UserAppService } from 'src/app/services/user-app.service';
import { EditUserRolesDialogComponent } from './edit-user-roles-dialog.component';

@Component({
  selector: 'app-user-management',
  template: `
    <div class="users-page">
      <div class="pagehead">
        <div>
          <h1>User management</h1>
          <p>Manage your team and what each person can do.</p>
        </div>
        <button
          type="button"
          class="btn-primary"
          routerLink="../add-users"
        >
          + Create user
        </button>
      </div>

      <div class="toolbar">
        <div class="searchbox">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" />
          </svg>
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="applyFilter()"
            placeholder="Search by name or email"
          />
        </div>
        <div class="count">{{ dataSource.filteredData.length }} users</div>
      </div>

      <div class="panel">
        <table mat-table [dataSource]="dataSource">
          <!-- NAME -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let user">
              <div class="uname">
                <span class="uav" [style.background]="avatarColor(user)">{{
                  initials(user)
                }}</span>
                <span class="dname">{{ displayName(user) }}</span>
              </div>
            </td>
          </ng-container>

          <!-- EMAIL -->
          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef>Email</th>
            <td mat-cell *matCellDef="let user" class="muted">{{ user?.email }}</td>
          </ng-container>

          <!-- ROLES -->
          <ng-container matColumnDef="roles">
            <th mat-header-cell *matHeaderCellDef>Roles</th>
            <td mat-cell *matCellDef="let user">
              <span class="role-chip" *ngFor="let role of user?.roles">{{
                role | textpretty | titlecase
              }}</span>
              <span class="muted" *ngIf="!(user?.roles && user.roles.length > 0)"
                >No roles</span
              >
            </td>
          </ng-container>

          <!-- ACTIONS -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="ta-right">Actions</th>
            <td mat-cell *matCellDef="let user" class="ta-right">
              <div class="row-actions">
                <button
                  type="button"
                  class="iconbtn"
                  matTooltip="Edit roles"
                  (click)="editRoles(user)"
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                       stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="iconbtn danger"
                  matTooltip="Remove user"
                  (click)="deleteUser(user)"
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                       stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M6 6l1 14h10l1-14" />
                  </svg>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>

          <tr class="empty-row" *matNoDataRow>
            <td class="empty-cell" [attr.colspan]="displayedColumns.length">
              No users found.
            </td>
          </tr>
        </table>
      </div>
    </div>
  `,
  styleUrls: ['./user-management.component.scss'],
})
export class UserManagementComponent implements OnInit {
  SAML_CONFIGURATION_STATUS: boolean = false;
  availableUsers: any;
  available_roles: any = ['customer_consultant', 'sales_rep'];

  searchTerm = '';
  displayedColumns: string[] = ['name', 'email', 'roles', 'actions'];
  dataSource = new MatTableDataSource<any>([]);

  private avatarPalette = [
    '#E60000', '#2563EB', '#7C3AED', '#0D9488', '#B45309', '#DB2777', '#0891B2', '#4F46E5',
  ];

  constructor(
    private userServices: UserAppService,
    private snackBar: SharedFeaturesService,
    private dialog: MatDialog,
    private settingServices: SettingsService
  ) {
    this.dataSource.filterPredicate = (user: any, filter: string) => {
      const name = this.displayName(user).toLowerCase();
      const email = (user?.email || '').toLowerCase();
      return name.includes(filter) || email.includes(filter);
    };
  }

  ngOnInit(): void {
    this.fetchAvailableUsers();
    this.settingServices.getSamlStatus().subscribe({
      next: (response: any) => {
        this.SAML_CONFIGURATION_STATUS = response?.saml;
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });
    this.userServices.getUserRoles().subscribe({
      next: (response: any) => {
        this.available_roles = response?.message;
      },
      error: (error: any) => {
        console.error('An error occurred ', error);
      },
    });
  }

  fetchAvailableUsers() {
    this.userServices.getUser().subscribe({
      next: (response: any) => {
        this.availableUsers = response?.msg;
        this.dataSource.data = this.availableUsers || [];
        this.applyFilter();
      },
      error: (error: any) => {
        console.error('An error occurred ', error);
      },
    });
  }

  applyFilter() {
    this.dataSource.filter = (this.searchTerm || '').trim().toLowerCase();
  }

  displayName(user: any): string {
    const full = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    return full || user?.username || user?.email || '';
  }

  initials(user: any): string {
    const first = (user?.first_name || '').trim();
    const last = (user?.last_name || '').trim();
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
    }
    const fallback = (user?.email || user?.username || '').trim();
    return (fallback.slice(0, 2) || '?').toUpperCase();
  }

  avatarColor(user: any): string {
    const key = (user?.email || user?.username || '').toLowerCase();
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash << 5) - hash + key.charCodeAt(i);
      // The bitwise form wraps the hash to 32 bits; Math.trunc would not, and would change avatar colours.
      hash |= 0; // NOSONAR typescript:S7767
    }
    const index = Math.abs(hash) % this.avatarPalette.length;
    return this.avatarPalette[index];
  }

  editRoles(user: any) {
    const dialogRef = this.dialog.open(EditUserRolesDialogComponent, {
      panelClass: 'edit-roles-dialog',
      autoFocus: false,
      data: { user, availableRoles: this.available_roles },
    });
    dialogRef.afterClosed().subscribe((result: string[] | null) => {
      if (Array.isArray(result)) {
        user.roles = result;
        this.updateRoles(user);
      }
    });
  }

  updateRoles(user: any) {
    this.userServices.updateUserRole(user).subscribe({
      next: (response: any) => {
        this.snackBar.openSnackBar(response?.msg, 'Close', 'success-snackbar');
        this.fetchAvailableUsers();
      },
      error: (error: any) => {
        console.error('an error occurred', error);
        this.snackBar.openSnackBar(
          error?.msg || error?.message,
          'Close',
          'error-snackbar'
        );
      },
    });
  }

  deleteUser(user: any) {
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Remove user',
        message: `Remove '${user.email}' from this workspace?`,
        confirmText: 'Remove',
        danger: true,
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.userServices.removeUser(user).subscribe({
          next: (response: any) => {
            this.fetchAvailableUsers();
            this.snackBar.openSnackBar(
              response?.msg,
              'Close',
              'success-snackbar'
            );
          },
          error: (error: any) => {
            this.snackBar.openSnackBar(error?.msg, 'Close', 'error-snackbar');
          },
        });
      }
    });
  }
}
